import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
    fetchCardVariations,
    createCardVariation,
    updateCardVariation,
    deleteCardVariation,
} from '../../api/cardVariation';
import LoadingSpinner from '../../components/LoadingSpinner';

const AdminVariationsPage = () => {
    const [variations, setVariations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ name: '', cssClass: '' });
    const [editingID, setEditingID] = useState(null);
    const [previewVariation, setPreviewVariation] = useState(null);

    const closePreview = useCallback(() => setPreviewVariation(null), []);

    const loadVariations = async () => {
        try {
            const data = await fetchCardVariations();
            setVariations(data);
        } catch {
            toast.error('Failed to load variations');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadVariations();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingID) {
                await updateCardVariation(editingID, form);
                toast.success('Variation updated');
            } else {
                await createCardVariation(form);
                toast.success('Variation created');
            }
            setForm({ name: '', cssClass: '' });
            setEditingID(null);
            await loadVariations();
        } catch {
            toast.error('Failed to save variation');
        }
    };

    const handleEdit = (variation) => {
        setEditingID(variation.variationID);
        setForm({ name: variation.name, cssClass: variation.cssClass });
    };

    const handleDelete = async (variationID) => {
        try {
            await deleteCardVariation(variationID);
            toast.success('Variation deleted');
            await loadVariations();
        } catch {
            toast.error('Failed to delete variation');
        }
    };

    const handleCancel = () => {
        setEditingID(null);
        setForm({ name: '', cssClass: '' });
    };

    if (loading) return <LoadingSpinner />;

    return (
        <div className="admin-page">
            <h2>Card Variations</h2>

            <form onSubmit={handleSubmit} className="admin-form">
                <div className="admin-field">
                    <label>Name</label>
                    <input
                        type="text"
                        value={form.name}
                        onChange={e => setForm({ ...form, name: e.target.value })}
                        required
                    />
                </div>
                <div className="admin-field">
                    <label>CSS Class</label>
                    <input
                        type="text"
                        value={form.cssClass}
                        onChange={e => setForm({ ...form, cssClass: e.target.value })}
                        required
                    />
                </div>
                <button type="submit">{editingID ? 'Update' : 'Create'}</button>
                {editingID && <button type="button" className="boring_button" onClick={handleCancel}>Cancel</button>}
            </form>

            <div className="table-scroll-wrapper">
                <table>
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Name</th>
                            <th>CSS Class</th>
                            <th>Preview</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {variations.map(v => (
                            <tr key={v.variationID}>
                                <td>{v.variationID}</td>
                                <td>{v.name}</td>
                                <td><code>{v.cssClass}</code></td>
                                <td>
                                    <div
                                        className={`card-container ${v.cssClass}`}
                                        style={{
                                            width: '90px',
                                            height: '126px',
                                            cursor: 'pointer',
                                            display: 'inline-block',
                                        }}
                                        onClick={() => setPreviewVariation(v)}
                                    >
                                        <div className="c card-front">
                                            <div
                                                className="card-face card-face--front"
                                                style={{
                                                    backgroundImage: 'url(/img/card/default.png)',
                                                    backgroundSize: 'cover',
                                                    backgroundPosition: 'center',
                                                }}
                                            />
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <div className="actions-cell">
                                        <button className="less-action" onClick={() => handleEdit(v)}>Edit</button>
                                        <button className="danger-action" onClick={() => handleDelete(v.variationID)}>Delete</button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {previewVariation && (
                <div className="variation-preview-overlay" onClick={closePreview}>
                    <div className="variation-preview-modal" onClick={e => e.stopPropagation()}>
                        <h3>{previewVariation.name}</h3>
                        <div
                            className={`card-container ${previewVariation.cssClass}`}
                        >
                            <div className="c card-front">
                                <div
                                    className="card-face card-face--front"
                                    style={{
                                        backgroundImage: 'url(/img/card/default.png)',
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                    }}
                                />
                            </div>
                        </div>
                        <button onClick={closePreview}>Close</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminVariationsPage;
